import React, { memo, useCallback, useEffect, useMemo} from "react"
import Select from "react-select"
import { formatDecimalPlaces } from "../../../utils/formatDecimalPlaces";
import { Toggle } from "../inputs/Toggle";
import { TruncatedInput } from "../inputs/TruncatedInput";
const cellClasses = "table-cell border p-4 text-sm text-slate-700"

/**
 * dropdown component for table cells
 * 
 * @param {Array} schemaOptions array of dropdown options derived from schema 
 * @param {string} selectValue the value for the current table cell, from data in parent
 * @param {function} onChange onChange function for handling input change, passed down from parent
 * @param {string} errorPlaceholder error message
 * @param {Function} validation passed in from schema - if subchain, we're checking if upstream and downstream sites look correct
 * @param {Object[]} agmRawData used to validate upstream/downstream sites for subchains
 * @param {Object} rowData data for entire row (all the data for the AGM, subchain, etc) - need data like lat/lng for validation
 * @param {string} schemaKey what column this component is in, with schema formatting. eg SC_Name, US_Site
 * @param {boolean} disabled if job is locked, may optionally want to disable this component
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - UI
 */

export const TableSelect = ({ schemaOptions, selectValue, onChange, errorPlaceholder, validation, agmRawData, rowData, schemaKey, disabled }) => {
  // If schemaOptions is an array of strings, format it into objects with value and label

  let options = [];
  let dynamicOptions = false; //boolean to tell if it's dynamic options - right now just agms on sc get the error response
  if (schemaOptions.every(item => typeof item === 'string')) {
    options = schemaOptions.map(option => ({ value: option, label: option })); //value and options just get placed if it's array of strings
  } else {
    options = schemaOptions;
    dynamicOptions = true
  }

  if ((!rowData.US_Site || !rowData.DS_Site) && validation && (rowData.lat && rowData.lng && agmRawData)) {
    let closestSegment = validation(rowData.lat, rowData.lng, agmRawData);

    rowData.US_Site = closestSegment.closestSegment.upstream.AGMID;
    rowData.DS_Site = closestSegment.closestSegment.downstream.AGMID;
  }


  // Find the option object where the value matches selectValue
  const selectedOption = options.find(option => option.label === selectValue || option.value === selectValue);

  // If no valid selectedOption, show error placeholder
  let valueToDisplay = selectedOption || { value: 'error', label: selectValue };

  //validation
  if (valueToDisplay.value !== 'error' && validation && (rowData.lat && rowData.lng && agmRawData)) {

    let closestSegment = validation(rowData.lat, rowData.lng, agmRawData);

    if(!closestSegment || !closestSegment.closestSegment) return

    if (schemaKey === 'US_Site' && rowData.US_Site !== closestSegment.closestSegment.upstream.AGMID) {
      valueToDisplay = { value: 'error', label: selectedOption.label }
      errorPlaceholder = 'US site appears incorrect';
    }

    else if (schemaKey === 'DS_Site' && rowData.DS_Site !== closestSegment.closestSegment.downstream.AGMID) {
      valueToDisplay = { value: 'error', label: selectedOption.label }
      errorPlaceholder = 'DS site appears incorrect';
    }
  }

  // Custom styles for react-select, changing the border color if there's an error
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: (dynamicOptions && valueToDisplay.value === 'error') ? 'red' : state.isFocused ? 'blue' : base.borderColor,
      boxShadow: (dynamicOptions && valueToDisplay.value === 'error') ? '0 0 0 1px red' : state.isFocused ? '0 0 0 1px blue' : base.boxShadow,
      '&:hover': {
        borderColor: (dynamicOptions && valueToDisplay.value === 'error') === 'error' ? 'red' : base['&:hover'].borderColor,
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999, // or higher if needed
    }),
  };

  return (
    <div className="select-control">
      <Select
        options={options}
        value={valueToDisplay}
        // value={selectedOption}
        // onChange={onChange}
        onChange={(selectedOption) => onChange(selectedOption)}
        styles={customStyles} // Apply dynamic error styles
        menuPosition="fixed"
        menuPortalTarget={document.body}
        className=""
        isDisabled={disabled}
      />
      {(dynamicOptions && valueToDisplay.value === 'error') && <div style={{ color: 'red', marginTop: '5px' }}>{errorPlaceholder || 'AGM Not Found'}</div>}
    </div>
  );
};

/**
 * input component for tables
 * 
 * @param {string} type type of input (text or number), originally derived from schema
 * @param {string} value the value for the current table cell, from data in parent
 * @param {function} onChange onChange function for handling input change, passed down from parent
 * @param {string} name
 * 
 * @returns {JSX.Element} 
 * 
 * @category Components - UI
 */
export const TableInput = React.memo(({
  type,
  value,
  onChange,
  name,
  disabled = null,
  schemaKey = null,
  agmData = null,
  rowData = null,
  calculation = null,
  subchains = null,
  rowIndex = null,
}) => {
  // Calculate Real_Chain_Delta
  useEffect(() => {
    if (
      schemaKey === 'Real_Chain_Delta' &&
      Array.isArray(agmData) &&
      rowData?.Real_Chain != null &&
      typeof calculation === 'function'
    ) {
      const newValue = Number(calculation(agmData, rowData));
      const current = Number(value);

      if (
        value == null ||
        !Number.isFinite(current) ||
        Number(newValue.toFixed(2)) !== Number(current.toFixed(2))
      ) {
        onChange({ target: { name, value: newValue } });
      }
    }
  }, [schemaKey, agmData, rowData, value, onChange, name, calculation]);

  // Calculate Dist_To_US or Dist_To_DS
  useEffect(() => {
    if (
      (schemaKey === 'Dist_To_US' || schemaKey === 'Dist_To_DS') &&
      Array.isArray(agmData) &&
      Array.isArray(subchains) &&
      String(rowData?.SC_Name || '').trim()
    ) {
      const newValue = Number(calculation(agmData, subchains, rowData));
      const currentValue = Number(value);

      const newFixed = Number(newValue.toFixed(3));
      const currentFixed = Number(currentValue.toFixed(3));

      if (!isNaN(newFixed) && newFixed !== currentFixed) {
        onChange({ target: { name, value: newFixed } });
      }
    }
  }, [schemaKey, agmData, subchains, rowData, value, onChange, name, calculation]);

  return (
    <TruncatedInput
      type={type}
      value={value ?? ''}
      onChange={onChange}
      name={name}
      disabled={disabled}
    />
  );
}, areEqual);

// Custom memo comparison
function areEqual(prevProps, nextProps) {
  return (
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.name === nextProps.name &&
    prevProps.schemaKey === nextProps.schemaKey &&
    prevProps.type === nextProps.type &&
    JSON.stringify(prevProps.rowData) === JSON.stringify(nextProps.rowData)
  );
}


/**
 * table header component 
 * 
 * @param {Object} schema object that describes how the data should be displayed, input types, required fields, column titles, etc
 * @param {boolean} canDeleteRow boolean that determines if a delete button needs to be added to the row
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - UI
 */

export const TableHeader = ({ schema, canDeleteRow }) => {
  return (
    <thead>
      <tr className='martrak-table-header-row'>

        {/* get column headers from schema */}
        {Object.keys(schema).map((key) => {
          return schema[key] ? (
            <th key={key} className={`${cellClasses} martrak-table-header`}>{schema[key].title}</th>
          ) : null;
        })}

        {/* if row can be deleted there will be an extra cell in each row to display delete icon - so in header, leave the cell blank */}
        {canDeleteRow ? (
          <th key="delete-column-header" className={`${cellClasses} martrak-table-header`}></th>
        ) : null}

      </tr>
    </thead>
  )
}


/**
 * memoized table row component - displays one row of data, will only update if one of the props changes, most likely rowData or validationErrors
 * 
 * @param {Object} rowData an object of data
 * @param {number} rowIndex row number, or index of this row in parent data array
 * @param {Object} schema object that describes how the data should be displayed, input types, required fields, column titles, etc
 * @param {function} handleFieldChange function that updates data in parent component based when an input changes
 * @param {function} setData setter function that sets the data in the parent component
 * @param {boolean} canDeleteRow boolean that determines if a delete button needs to be added to the row
 * @param {function} handleDeleteRow function that deletes the row from the data in parent component
 * @param {function} handleToggleAPI function for toggle component that directly calls API
 * @param {Object[]} validationErrors an array that contains the row number and column where errors occur: eg {row: 1, column: "Site_Name"}
 * @param {Object[]} agmOptions dropdown options for AGMs, can be used for subchain US/DS site options
 * @param {Object[] | null} agmRawData all the AGM data not included in agmOptions
 * @param {Function} requestConfirm for confirming if user wants to delete row, opens modal
 * @param {Object[]} subchains for calculating subchain US/DS sites
 * @param {boolean} jobIsLocked may optionally disable inputs if job locked - determined by schema readOnlyWhenLocked field
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - UI
 */

export const TableRow = memo(({
  rowData,
  rowIndex,
  schema,
  handleFieldChange,
  setData,
  canDeleteRow,
  handleDeleteRow,
  handleToggleAPI,
  validationErrors,
  agmOptions,
  agmRawData = null,
  requestConfirm,
  subchains,
  jobIsLocked
}) => {
  const isCellInvalid = useCallback((rowIndex, columnKey) => {
    return validationErrors.some(error => error.row === rowIndex && error.column === columnKey);
  }, [validationErrors]);

  // Memoize onChange functions for each cell
  const onChangeHandlers = useMemo(() => {
    const handlers = {};

    Object.keys(schema).forEach((key) => {
      handlers[key] = (e) => {
        const value = e?.target?.value ?? e; // Support e.target.value or custom object
        handleFieldChange(rowIndex, key, value, setData);
      };
    });

    return handlers;
  }, [schema, handleFieldChange, rowIndex, setData]);

  return (
    <tr key={`row-${rowIndex}`} className="martrak-table-row">
      {Object.keys(schema).map((schemaKey) => {
        const col = schema[schemaKey];
        const disabledJobLocked = col.readOnlyWhenLocked && jobIsLocked

        if (!col) return null;

        const commonProps = {
          name: `${rowIndex}-${schemaKey}`,
          disabled: col.disabled || disabledJobLocked || null,
          onChange: onChangeHandlers[schemaKey],
        };

        return (
          <td
            key={`${rowIndex}-cell-${schemaKey}`}
            className={isCellInvalid(rowIndex, schemaKey) ? `table-cell border p-4 martrak-error-border` : `table-cell border p-4`}
          >
            {col.inputType === 'text' ? (
              <TableInput
                type="text"
                value={rowData[schemaKey]}
                {...commonProps}
              />
            ) : col.inputType === 'number' ? (
              <TableInput
                type="number"
                value={typeof rowData[schemaKey] === 'number' ? formatDecimalPlaces(rowData[schemaKey], 5) : rowData[schemaKey]}
                {...commonProps}
                agmData={agmRawData}
                rowData={rowData}
                schemaKey={schemaKey}
                subchains={subchains}
                calculation={col.calculation || null}
                rowIndex={rowIndex}
              />
            ) : col.inputType === 'dropdown' ? (
              <TableSelect
                schemaOptions={col.options === 'agms' ? agmOptions : col.options}
                selectValue={rowData[schemaKey]}
                onChange={(selectedOption) => handleFieldChange(rowIndex, schemaKey, selectedOption, setData)}
                validation={col.validation || null}
                agmRawData={agmRawData}
                rowData={rowData}
                schemaKey={schemaKey}
                disabled={disabledJobLocked}
              />
            ) : col.inputType === 'toggle-state' ? (
              <Toggle
                checked={rowData[schemaKey]}
                id={`${rowIndex}-${schemaKey}-toggle`}
                labelText=""
                locked={col.toggleLocked || disabledJobLocked || false}
                onChange={(e) => handleFieldChange(rowIndex, schemaKey, e, setData)}
              />
            ) : col.inputType === 'toggle-api' ? (
              <Toggle
                checked={rowData[schemaKey]}
                id={`${rowIndex}-${schemaKey}-toggle`}
                labelText=""
                locked={disabledJobLocked || false}
                onChange={(e) => handleToggleAPI(rowIndex, e)}
              />
            ) : (
              rowData[schemaKey]
            )}
          </td>
        );
      })}

      {canDeleteRow && !jobIsLocked && (
        <td className="flex justify-center items-center">
          <i
            className="fa-regular fa-trash-can"
            onClick={async () => {
              const confirm = await requestConfirm('Are you sure you want to delete this item?');
              if (confirm) handleDeleteRow(rowData, rowIndex);
            }}
            style={{ color: 'red', fontSize: '20px', cursor: 'pointer' }}
          />
        </td>
      )}
    </tr>
  );
}, areRowsEqual);

function areRowsEqual(prev, next) {
  return (
    JSON.stringify(prev.rowData) === JSON.stringify(next.rowData) &&
    prev.validationErrors === next.validationErrors &&
    prev.agmRawData === next.agmRawData &&
    prev.subchains === next.subchains &&
    prev.schema === next.schema
  );
}
